import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, RefreshCcw, Home, MessageSquare } from 'lucide-react';
import { THEMES, DEFAULT_LOGO_URL } from '../components/landing/landingConstants';
import { Button } from '../components/ui/button';

/**
 * Branded Maintenance page — "Half Time."
 *
 * Shown during planned deploys / DB migrations / when a maintenance flag is on.
 * Designed to mirror the Yellow Card (404) and Red Card (500) treatment so
 * the broken-state set feels like one family.
 *
 * Self-contained: depends only on landing constants + lucide. If/when we ship
 * a static HTML fallback (so the page works even when the React bundle is
 * down), copy the rendered markup verbatim — no JS required to display.
 */
export default function MaintenancePage() {
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => localStorage.getItem('landingTheme') || 'dark');
  const isDark = theme === 'dark';
  const t = THEMES[theme];

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('landingTheme', next);
  };

  return (
    <div
      className={`min-h-screen ${t.bg} ${t.text} relative overflow-hidden`}
      data-testid="maintenance-page"
    >
      <style>{`
        @keyframes ref-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.03); }
        }
        .ref-img { animation: ref-pulse 3.2s ease-in-out infinite; }

        /* Subtle green pitch stripes — calmer than red/yellow, fits "team talk" vibe */
        .pitch-stripe-green::before {
          content: "";
          position: absolute;
          inset: 0;
          background: repeating-linear-gradient(
            90deg,
            rgba(0, 122, 51, 0.06) 0px,
            rgba(0, 122, 51, 0.06) 60px,
            transparent 60px,
            transparent 120px
          );
          pointer-events: none;
        }
      `}</style>

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className={`fixed bottom-6 left-6 z-50 p-3 rounded-full shadow-lg transition-all ${
          isDark ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' : 'bg-[#007a33] text-white hover:bg-[#005c26]'
        }`}
        aria-label="Toggle theme"
        data-testid="theme-toggle"
      >
        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      {/* Logo top-left */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 z-20 flex items-center gap-2 group"
        data-testid="maintenance-logo"
      >
        <img
          src={DEFAULT_LOGO_URL}
          alt="Grassroots Coaching"
          className="w-10 h-10 object-contain group-hover:opacity-80 transition-opacity"
        />
        <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'} hidden sm:inline`}>
          Grassroots Coaching
        </span>
      </button>

      <main className="relative pitch-stripe-green min-h-screen flex items-center justify-center px-4 py-20">
        <div className="max-w-3xl w-full text-center relative z-10">
          {/* Pre-headline label */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#007a33]/10 border border-[#007a33]/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#007a33] animate-pulse" />
            <span className="text-xs font-semibold tracking-widest uppercase text-[#007a33]">
              Match report · Maintenance
            </span>
          </div>

          {/* Ref blowing whistle */}
          <div className="flex items-center justify-center mb-8 select-none">
            <img
              src="https://customer-assets.emergentagent.com/job_b281913e-4091-4a90-899a-c0e767ce9196/artifacts/aep9u6k8_error-halftime.png"
              alt="Referee blowing the half time whistle"
              className="w-[220px] h-[220px] sm:w-[280px] sm:h-[280px] object-cover rounded-full ref-img"
              draggable="false"
              data-testid="maintenance-referee-img"
            />
          </div>

          <h1 className={`text-4xl sm:text-6xl font-black tracking-tight mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Half Time.
          </h1>

          <p className={`text-base sm:text-lg max-w-xl mx-auto mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            The ref's blown the whistle — we're in the dressing room for a quick team talk.
          </p>
          <p className={`text-sm max-w-xl mx-auto mb-8 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            Back on the pitch in a few minutes. Grab a drink, we won't be long.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              size="lg"
              onClick={() => window.location.reload()}
              className="bg-[#007a33] hover:bg-[#005c26] text-white px-6"
              data-testid="maintenance-retry-btn"
            >
              <RefreshCcw className="w-4 h-4 mr-2" />
              Check the team sheet again
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/')}
              className={isDark ? 'border-gray-700 text-white hover:bg-gray-800' : ''}
              data-testid="maintenance-home-btn"
            >
              <Home className="w-4 h-4 mr-2" />
              Back to the touchline
            </Button>
          </div>

          <p className={`text-xs mt-10 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            Something urgent?{' '}
            <button
              onClick={() => window.location.href = 'mailto:support@grassrootscoaching.app?subject=Maintenance%20-%20urgent'}
              className="inline-flex items-center gap-1 text-[#007a33] hover:underline font-medium"
              data-testid="maintenance-support-link"
            >
              <MessageSquare className="w-3 h-3" />
              Have a word with the gaffer
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
